import type { PlayableRecording } from "@bandwith/recordings";
import { formatRelativeTime } from "@bandwith/time";
import { MessageCircle } from "lucide-react";
import type { FeedbackItem as FeedbackItemType } from "../lib/api";
import { api } from "../lib/api";
import { printsTime, runSegments } from "../lib/feedbackRuns";
import { FeedbackForm, type FeedbackTarget } from "./FeedbackForm";
import { FeedbackItem } from "./FeedbackItem";

type Props = {
  feedback: FeedbackItemType[];
  /** What the feedback attaches to: a song or a setlist */
  target: FeedbackTarget;
  recordings: PlayableRecording[];
  isAdmin: boolean;
  /** Amplify controls only exist on song pages; omit them for setlists */
  songAuthorId?: string | null;
  amplifiedCommentId?: string | null;
  onAmplifiedChanged?: (feedbackId: string | null) => void;
  onFeedbackCreated?: (newFeedback: FeedbackItemType) => void;
  onFeedbackUpdated?: (id: string, body: string) => void;
  onFeedbackDeleted?: (id: string) => void;
  /** Pre-filled anchor markup from text selection */
  pendingAnchor?: string | null;
  /** Called after the anchor has been consumed */
  onAnchorConsumed?: () => void;
  /** Called when a lyrics anchor chip is clicked */
  onLyricsAnchorClick?: (anchorText: string) => void;
};

export function FeedbackList({
  feedback,
  target,
  recordings,
  isAdmin,
  songAuthorId,
  amplifiedCommentId,
  onAmplifiedChanged,
  onFeedbackCreated,
  onFeedbackUpdated,
  onFeedbackDeleted,
  pendingAnchor,
  onAnchorConsumed,
  onLyricsAnchorClick,
}: Props) {
  const canAmplify = "songId" in target && onAmplifiedChanged !== undefined;

  // On a song page the song is known, so a recording chip names the take by
  // filename; on a setlist page the song is what matters.
  const recordingName = "songId" in target ? "filename" : "songTitle";

  // Consecutive comments by one author render as a single card, and each
  // comment's time is printed only where a reader scanning down the card
  // would learn something from it. Labels are formatted once here so every
  // row shares one "now".
  const segments = runSegments(
    feedback.map((item) =>
      item.id === amplifiedCommentId ? null : item.authorId,
    ),
  );
  const relativeTimes = feedback.map((item) =>
    formatRelativeTime(item.createdAt),
  );
  const timePrinted = printsTime(relativeTimes, segments);

  const handleAmplifyToggle = async (feedbackId: string) => {
    if (!canAmplify) return;
    const previousAmplifiedId = amplifiedCommentId ?? null;
    const newAmplifiedId =
      previousAmplifiedId === feedbackId ? null : feedbackId;
    onAmplifiedChanged(newAmplifiedId);

    try {
      await api.songs[":id"]["amplified-comment"].$put({
        param: { id: target.songId },
        json: { feedbackId: newAmplifiedId },
      });
    } catch (error) {
      console.error("Failed to update amplified comment:", error);
      onAmplifiedChanged(previousAmplifiedId);
    }
  };

  return (
    <div className="space-y-4">
      <FeedbackForm
        target={target}
        onFeedbackCreated={onFeedbackCreated}
        pendingAnchor={pendingAnchor}
        onAnchorConsumed={onAnchorConsumed}
      />

      {feedback.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border-subtle bg-bg-secondary/50 py-6 text-center">
          <MessageCircle
            className="h-5 w-5 text-text-muted"
            aria-hidden="true"
          />
          <p className="mt-2 text-sm text-text-muted">No feedback yet</p>
        </div>
      ) : (
        <div className="flex flex-col">
          {feedback.map((item, index) => (
            <FeedbackItem
              key={item.id}
              feedback={item}
              segment={segments[index] ?? "only"}
              relativeTime={relativeTimes[index] ?? ""}
              printsTime={timePrinted[index] ?? true}
              recordings={recordings}
              recordingName={recordingName}
              isAdmin={isAdmin}
              songAuthorId={songAuthorId}
              isAmplified={amplifiedCommentId === item.id}
              onAmplifyToggle={canAmplify ? handleAmplifyToggle : undefined}
              onFeedbackUpdated={onFeedbackUpdated}
              onFeedbackDeleted={onFeedbackDeleted}
              onLyricsAnchorClick={onLyricsAnchorClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}
